import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  User,
  Building,
  Globe,
  Mail,
  Phone,
  MapPin,
  Upload,
  FileText,
  Download,
} from "lucide-react";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPRDetail = () => {
  const { prId } = useParams();
  const navigate = useNavigate();
  const [pr, setPr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionReason, setActionReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPR();
  }, [prId]);

  const fetchPR = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/press-releases/${prId}`, {
        credentials: "include",
      });
      if (response.ok) {
        setPr(await response.json());
      } else {
        toast.error("Press release not found");
        navigate("/admin/press-releases");
      }
    } catch (error) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    if ((action === "reject" || action === "request_revision") && !actionReason) {
      toast.error("Please provide a reason");
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/press-releases/${prId}/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          action,
          reason: actionReason || null,
        }),
      });

      if (response.ok) {
        toast.success(
          action === "approve"
            ? "Press release approved"
            : action === "reject"
            ? "Press release rejected"
            : "Revision requested"
        );
        fetchPR();
        setActionReason("");
      } else {
        toast.error("Action failed");
      }
    } catch (error) {
      toast.error("Error");
    } finally {
      setProcessing(false);
    }
  };

  const handleReportUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/press-releases/${prId}/upload-report`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        toast.success("Report uploaded successfully! Customer has been notified.");
        fetchPR();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload report");
      }
    } catch (error) {
      toast.error("Upload error");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDownloadReport = () => {
    if (pr?.report_file) {
      window.open(`${API_URL}/api/reports/${pr.report_file}`, "_blank");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-100 text-slate-700",
      pending: "bg-amber-50 text-amber-700",
      scheduled: "bg-blue-50 text-blue-700",
      published: "bg-emerald-50 text-emerald-700",
      rejected: "bg-red-50 text-red-700",
      needs_revision: "bg-orange-50 text-orange-700",
    };
    return styles[status] || styles.draft;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!pr) return null;

  return (
    <div className="space-y-6 max-w-5xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/admin/press-releases")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-heading font-bold text-slate-900">PR #{pr.pr_id}</h1>
              <Badge className={`${getStatusBadge(pr.status)} capitalize`}>
                {pr.status.replace("_", " ")}
              </Badge>
            </div>
            <p className="text-slate-500 text-sm mt-1">
              Submitted {format(new Date(pr.created_at), "MMMM d, yyyy 'at' h:mm a")}
            </p>
          </div>
        </div>
      </div>

      {/* Submitter Info */}
      {pr.user && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Submitted By</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">{pr.user.name}</p>
                <p className="text-sm text-slate-500">{pr.user.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report Upload Section */}
      <Card className="border-blue-200">
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Distribution Report
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pr.report_file ? (
            <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-emerald-600" />
                <div>
                  <p className="font-medium text-slate-900">Report Uploaded</p>
                  <p className="text-sm text-slate-500">
                    Uploaded on {pr.report_uploaded_at ? format(new Date(pr.report_uploaded_at), "MMM d, yyyy 'at' h:mm a") : "N/A"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={handleDownloadReport}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleReportUpload}
                    accept=".pdf,.doc,.docx"
                    className="hidden"
                  />
                  <Button variant="outline" asChild disabled={uploading}>
                    <span>
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? "Uploading..." : "Replace"}
                    </span>
                  </Button>
                </label>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">No Report Uploaded</p>
                  <p className="text-sm text-slate-500">Upload a PDF or DOC report for this press release</p>
                </div>
              </div>
              <label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleReportUpload}
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                />
                <Button className="bg-blue-600 hover:bg-blue-700" asChild disabled={uploading}>
                  <span>
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Uploading..." : "Upload Report"}
                  </span>
                </Button>
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin Actions */}
      {pr.status === "pending" && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Pending Review
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Feedback / Reason (required for rejection or revision)</Label>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Enter feedback for the submitter..."
                className="mt-2"
                data-testid="action-reason-textarea"
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => handleAction("approve")}
                disabled={processing}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="approve-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve & Publish
              </Button>
              <Button
                onClick={() => handleAction("request_revision")}
                disabled={processing}
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="revision-btn"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Request Revision
              </Button>
              <Button
                onClick={() => handleAction("reject")}
                disabled={processing}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="reject-btn"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Content Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Press Release Content</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-slate-500">Topic</Label>
            <p className="mt-1 text-slate-900">{pr.topic || "Not specified"}</p>
          </div>

          <div>
            <Label className="text-slate-500">Headline</Label>
            <h2 className="mt-1 text-xl font-semibold text-slate-900">{pr.title}</h2>
          </div>

          <div>
            <Label className="text-slate-500">Location</Label>
            <p className="mt-1 text-slate-900">
              {pr.city}, {pr.country}
            </p>
          </div>

          <div>
            <Label className="text-slate-500">Summary</Label>
            <p className="mt-1 text-slate-700 italic">{pr.summary || "No summary provided"}</p>
          </div>

          <Separator />

          <div>
            <Label className="text-slate-500">Body</Label>
            <div
              className="mt-2 prose max-w-none text-slate-700"
              dangerouslySetInnerHTML={{ __html: pr.body || "<p>No content</p>" }}
            />
          </div>

          {pr.company_boilerplate && (
            <>
              <Separator />
              <div>
                <Label className="text-slate-500">Company Boilerplate</Label>
                <p className="mt-1 text-slate-700">{pr.company_boilerplate}</p>
              </div>
            </>
          )}

          {pr.video_url && (
            <div>
              <Label className="text-slate-500">Video URL</Label>
              <a
                href={pr.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 text-blue-600 hover:underline block"
              >
                {pr.video_url}
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Distribution Channels */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Distribution Channels</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {pr.distribution_channels?.length > 0 ? (
              pr.distribution_channels.map((ch, i) => (
                <Badge key={i} variant="outline" className="px-3 py-1">
                  {ch}
                </Badge>
              ))
            ) : (
              <p className="text-slate-500">No channels selected</p>
            )}
          </div>
          <div className="mt-4 flex items-center gap-4 text-sm text-slate-600">
            <span>
              Timing:{" "}
              <strong>{pr.publish_timing === "asap" ? "As soon as possible" : "Scheduled"}</strong>
            </span>
            {pr.white_label && <Badge className="bg-purple-100 text-purple-700">White Label</Badge>}
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Public Contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pr.public_org && (
              <div className="flex items-center gap-2 text-sm">
                <Building className="h-4 w-4 text-slate-400" />
                <span>{pr.public_org}</span>
              </div>
            )}
            {pr.public_website && (
              <div className="flex items-center gap-2 text-sm">
                <Globe className="h-4 w-4 text-slate-400" />
                <span>{pr.public_website}</span>
              </div>
            )}
            {pr.public_name && (
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-slate-400" />
                <span>{pr.public_name}</span>
              </div>
            )}
            {pr.public_email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-slate-400" />
                <span>{pr.public_email}</span>
              </div>
            )}
            {pr.public_phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-slate-400" />
                <span>{pr.public_phone}</span>
              </div>
            )}
            {pr.public_address && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-slate-400" />
                <span>{pr.public_address}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Internal Contact (Not Published)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pr.internal_org && (
              <div className="flex items-center gap-2 text-sm">
                <Building className="h-4 w-4 text-slate-400" />
                <span>{pr.internal_org}</span>
              </div>
            )}
            {pr.internal_website && (
              <div className="flex items-center gap-2 text-sm">
                <Globe className="h-4 w-4 text-slate-400" />
                <span>{pr.internal_website}</span>
              </div>
            )}
            {pr.internal_name && (
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-slate-400" />
                <span>{pr.internal_name}</span>
              </div>
            )}
            {pr.internal_email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-slate-400" />
                <span>{pr.internal_email}</span>
              </div>
            )}
            {pr.internal_phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-slate-400" />
                <span>{pr.internal_phone}</span>
              </div>
            )}
            {pr.internal_address && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="h-4 w-4 text-slate-400" />
                <span>{pr.internal_address}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rejection History */}
      {pr.rejection_reason && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-base font-medium text-red-700">
              Previous Feedback
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-700">{pr.rejection_reason}</p>
            {pr.rejection_date && (
              <p className="text-sm text-slate-500 mt-2">
                Sent on {format(new Date(pr.rejection_date), "MMMM d, yyyy")}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminPRDetail;
